import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
// import { getHealthInsuranceQuotations } from "../../../services/apiService"; // Uncomment and implement if available
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";

const HealthInsuranceQuotation = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

const [rowData, setRowData] = useState([
  {
    id: 1,
    insurer: "HDFC Ergo",
    product: "Health Suraksha",
    policyHolder: "Rahul Sharma",
    quotationNo: "QTN001",
    quotationDate: "2025-07-25"
  },
  {
    id: 2,
    insurer: "Tata AIG",
    product: "MediCare",
    policyHolder: "Priya Verma",
    quotationNo: "QTN002",
    quotationDate: "2025-07-26"
  },
  {
    id: 3,
    insurer: "ICICI Lombard",
    product: "Complete Health",
    policyHolder: "Amit Singh",
    quotationNo: "QTN003",
    quotationDate: "2025-07-27"
  }
]);

    
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("make");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [totalCount, setTotalCount] = useState();

  useEffect(() => {
    const options = [
      { value: "insurer", label: "Insurer" },
      { value: "product", label: "Product" },
      { value: "policyHolder", label: "Policy Holder" },
      { value: "quotationNo", label: "Quotation No" },
      { value: "quotationDate", label: "Quotation Date"},
    ];
    setColumnOptions(options);
    // fetchHealthInsuranceQuotationsData(); // Uncomment if API is available
    setTotalCount(rowData.length);
  }, []);

  //for mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Uncomment and implement if API is available
  // const fetchHealthInsuranceQuotationsData = async () => {
  //   try {
  //     setLoading(true);
  //     const requestPayload = {
  //       TObject: {
  //         CreatedBy: 1,
  //         filterColumn: isFilterApplied ? filterColumn : null,
  //         filterValue: isFilterApplied ? filterValue.trim() : null,
  //         sortField: sortField,
  //         sortOrder: sortDirection,
  //       },
  //     };
  //     const response = await getHealthInsuranceQuotations(requestPayload);
  //     const data = response?.data?.tObjectLst || [];
  //     setRowData(data);
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to fetch health insurance quotation data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleFilter = async () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
    // await fetchHealthInsuranceQuotationsData(); // Uncomment if API is available
  };

  const clearFilters = async () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    // await fetchHealthInsuranceQuotationsData(); // Uncomment if API is available
  };

  const handleSort = async (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    // await fetchHealthInsuranceQuotationsData(); // Uncomment if API is available
  };

  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };
  
  // Update header components when sort state changes
  useEffect(() => {
    if (columnDefs.length > 0) {
      const updatedColumnDefs = columnDefs.map(col => {
        if (col.headerComponent) {
          return {
            ...col,
            headerComponent: createHeaderComponent(col.field, col.headerName)
          };
        }
        return col;
      });
      setColumnDefs(updatedColumnDefs);
    }
  }, [sortField, sortDirection]);

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Insurer",
        field: "insurer",
        flex: 1,
        headerComponent: createHeaderComponent("insurer", "Insurer"),
        suppressMenu: true,
      },
      {
        headerName: "Product",
        field: "product",
        flex: 1,
        headerComponent: createHeaderComponent("product", "Product"),
        suppressMenu: true,
      },
      {
        headerName: "Policy Holder",
        field: "policyHolder",
        flex: 1,
        headerComponent: createHeaderComponent("policyHolder", "Policy Holder"),
        suppressMenu: true,
      },
      {
        headerName: "Quotation No ",
        field: "quotationNo",
        flex: 1,
        headerComponent: createHeaderComponent("quotationNo", "Quotation No "),
        suppressMenu: true,
      },
      {
        headerName: "Quotation Date",
        field: "quotationDate",
        flex: 1,
        headerComponent: createHeaderComponent("quotationDate", "Quotation Date"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1" >
            <button
              className="edit-button"
              onClick={() => navigate(`/vehicle-master/edit/${params.data.id}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen size={18}  color="#787474" className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.id);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 size={18}  color="#787474" className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
  }, []);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.id);
      setSelectedNames(selectedNodes.map((node) => node.data.product));

      if (selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0) {
        setAllSelected(true);
        setDeleteMode("multi");
        onOpen();
      } else {
        setAllSelected(false);
      }
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };

  const onOpen = () => {
    if (!gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      selectedIdsRef.current = selectedNodes.map((node) => node.data.id);
      setSelectedNames(selectedNodes.map((node) => node.data.product));
      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = () => {
    setRowData((prev) => {
      if (deleteMode === "multi" || allSelected) {
        return prev.filter((row) => !selectedIdsRef.current.includes(row.id));
      } else {
        return prev.filter((row) => row.id !== deleteId);
      }
    });
   
    toast.success("Vehicle deleted successfully");
    onClose();
  };

  const paginatedData = rowData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil(rowData.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [rowData, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      try {
        gridRef.current.api.forEachNode((node) => {
          if (selectedIdsRef.current.includes(node.data.id)) {
            node.setSelected(true);
          }
        });
      } catch (error) {
        console.error("Error in preserveSelection effect:", error);
      }
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Health Insurance Quotation List</h2>
        <button
          className="px-4 py-2 addListTable-button"
          onClick={() => navigate("/health-insurance/add")}
        >
          Add
        </button>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        paginatedData={paginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={loading}
        handleItemsPerPageChange={handleItemsPerPageChange}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${paginatedData.length} vehicles on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected vehicles?`
              : "Are you sure you want to delete this vehicle?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default HealthInsuranceQuotation;
